import { useLayoutEffect, useMemo, useRef } from 'react'

import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'
import {
    CustomCodeFieldSourceRef,
    CustomCodeWidgetType,
} from 'features/views/LayoutEditor/widgets/CustomCodeWidget/types'
import { useRecordManagerContext } from 'features/views/RecordManager/useRecordManagerContext'

export function useCustomCodeWidgetState(widget: CustomCodeWidgetType) {
    const { fields } = useLayoutEditorContext()
    const { record, isLoadingSlow } = useRecordManagerContext()

    const { source } = widget.attrs

    const field =
        source.type === 'field'
            ? fields.find((f) => f.api_name === (source as CustomCodeFieldSourceRef)?.fieldApiName)
            : undefined
    const code = source.type === 'inline' ? source.text : getValueFromField(field, record)

    const elementRef = useRef<HTMLDivElement>(null)
    useLayoutEffect(() => {
        if (!elementRef.current) return

        const range = document.createRange()
        range.selectNode(elementRef.current)
        const documentFragment = range.createContextualFragment(code)

        elementRef.current.innerHTML = ''
        elementRef.current.append(documentFragment)
    }, [code])

    return useMemo(
        () => ({
            code,
            isLoading: isLoadingSlow,
            elementRef,
        }),
        [code, isLoadingSlow, elementRef]
    )
}

function getValueFromField(field?: FieldDto, record?: RecordDto) {
    if (!field || !record) return ''

    const value = record[field.api_name]
    if (field.type === 'multi_file') {
        if (Array.isArray(value)) {
            return value[0]?.url ?? ''
        } else if (value && typeof value === 'object') {
            return (value as any).url ?? ''
        }
    } else if (field.type === 'document') {
        return value.plainTextContent ?? ''
    }

    return value ?? ''
}
